import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { ROOT_URL } from '../../api/ApiConfig';

const initialState = {
  items: [],
  page: 1,
  limit: 10,
  total: 0,
  totalPages: 0,
  loading: false,
  error: null,
  /** Once true, revisits refetch without full-page loading flash (list may be empty). */
  hasLoadedOnce: false,
  lastFetchedGroupId: null,
};

export const fetchAnalyticsExports = createAsyncThunk(
  'analyticsExportsList/fetchAnalyticsExports',
  async ({ page = 1, limit = 10, group_id } = {}, { getState, rejectWithValue }) => {
    try {
      const { loginToken } = getState();
      const authToken = loginToken?.response?.token;
      const businessId = loginToken?.response?.user?.business_id;

      if (!businessId) {
        return rejectWithValue({ message: 'Missing business id' });
      }

      const params = { page, limit };
      if (group_id) {
        params.group_id = group_id;
      }

      const response = await axios.get(
        `${ROOT_URL}/analytics/exports/${businessId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
          params,
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: 'Failed to fetch analytics exports' }
      );
    }
  }
);

const analyticsExportsListSlice = createSlice({
  name: 'analyticsExportsList',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAnalyticsExports.pending, (state, action) => {
        const groupId = action.meta?.arg?.group_id ?? null;
        const sameGroup =
          state.lastFetchedGroupId != null &&
          String(state.lastFetchedGroupId) === String(groupId);
        // Soft-load only for same-group revisits; show skeleton when group changes.
        state.loading = !(state.hasLoadedOnce && sameGroup);
        if (!sameGroup) {
          state.items = [];
        }
        state.error = null;
      })
      .addCase(fetchAnalyticsExports.fulfilled, (state, action) => {
        state.loading = false;
        state.hasLoadedOnce = true;
        state.lastFetchedGroupId = action.meta?.arg?.group_id ?? null;
        const d = action.payload?.data;
        if (d) {
          state.items = Array.isArray(d.items) ? d.items : [];
          state.page = typeof d.page === 'number' ? d.page : 1;
          state.limit = typeof d.limit === 'number' ? d.limit : 10;
          state.total = typeof d.total === 'number' ? d.total : 0;
          state.totalPages = typeof d.totalPages === 'number' ? d.totalPages : 0;
        }
      })
      .addCase(fetchAnalyticsExports.rejected, (state, action) => {
        state.loading = false;
        state.hasLoadedOnce = false;
        state.lastFetchedGroupId = action.meta?.arg?.group_id ?? null;
        const p = action.payload;
        state.error =
          (typeof p === 'object' && p?.message) ||
          (typeof p === 'string' ? p : null) ||
          'Failed to fetch analytics exports';
        state.items = [];
      });
  },
});

export default analyticsExportsListSlice.reducer;
